// What Sirra says during onboarding. Each line has an id: its spoken audio is recorded once, in a
// natural female voice, by `pnpm --filter @hrms/web voice:generate`, saved as a small MP3, and
// played from /voice/sirra/<id>.mp3. The text is also the on-screen caption. If you change a
// line's text, run the generator again with `-- --all` (or delete that line's .mp3 first).
//
// The sentences avoid symbols and read well out loud.

import { FILE_RULES, wrongFileTypeText, type FileRuleKey } from "./fileTypes.js";

export interface SirraLine {
  id: string;
  text: string;
  /** The line says the assistant's name, so it is only played as recorded while that name is "Sirra". */
  saysName?: boolean;
}

const STEP_TEXT = [
  "First, your documents. Please read and accept the data protection note. Then upload your Aadhaar, PAN, bank proof and resume, and your degree and marksheet. I'll check each one and fill in your details for you.",
  "Now your personal information. I've filled in what I could read from your documents. Please check your name, date of birth and contact details, and correct anything that looks wrong.",
  "Next, your address. Type your pincode and I'll fill in the city and state. If your permanent address is the same as your current one, just tick the box.",
  "Please add at least one emergency contact: someone we can reach if something happens. Enter their name, relationship and phone number.",
  "Now your bank details, which are used for your salary. If you uploaded a cancelled cheque I've filled them in. Please double check the account number and the IFSC code.",
  "Here is your education and work history, read from your resume. Please review it, and add your references: people who can speak about your work.",
  "Almost done! This is the equipment and access your department provides. Have a look, then continue.",
  "That's everything. Review your details, read and acknowledge the handbook, then submit. HR will review it and welcome you aboard.",
];

export const INTRO: SirraLine = {
  id: "intro",
  text: "Hello! Welcome aboard. I'm Sirra, your virtual HR assistant. I'll guide you through your onboarding one step at a time, and I'll check your documents as you upload them. Let's get started.",
  saysName: true,
};

// The Sign Documents step (handbook, leave policy). Its own id, so it never plays
// another step's recording; until the generator records it, it shows as a caption.
export const SIGN_DOCUMENTS_LINE: SirraLine = {
  id: "step-sign-documents",
  text: "Now the company policies HR has asked you to sign. Open each one with View and sign, read it to the end, and tick the box, and your signature appears. Click Sign, and you can download your signed copy straight away.",
};

export const stepLine = (step: number): SirraLine => {
  const i = Math.max(0, Math.min(step, STEP_TEXT.length - 1));
  return { id: `step-${i}`, text: STEP_TEXT[i]! };
};

const DOC_LABELS: Record<string, string> = {
  aadhaar: "Aadhaar card",
  pan: "PAN card",
  bank: "bank document",
  resume: "resume",
  degree: "degree certificate",
  marksheet: "marksheet",
};

export type DocKey = keyof typeof DOC_LABELS;

export const docAccepted = (doc: DocKey): SirraLine => ({
  id: `ok-${doc}`,
  text: `Thank you. I've checked your ${DOC_LABELS[doc]} and it looks good.`,
});

export const UPLOAD_FAILED: SirraLine = { id: "no-upload-failed", text: "That upload didn't work. Please try again." };
export const READ_FAILED: SirraLine = { id: "no-read-failed", text: "I saved your file, but I couldn't read the details from it. Please enter them yourself." };

export const FORM11_RECEIVED: SirraLine = { id: "ok-form11", text: "Thank you, I've received your Form 11." };

/** What she says when the file is not the type the field takes: one recording per kind of field. */
export const wrongFileTypeLine = (rule: FileRuleKey): SirraLine => ({
  id: `no-file-type-${rule.replace(/[A-Z]/g, (c) => `-${c.toLowerCase()}`)}`,
  text: wrongFileTypeText(FILE_RULES[rule]),
});

const REJECTIONS: Record<string, string> = {
  WRONG_DOCUMENT: "This doesn't look like the right document. Please upload the correct one.",
  SUSPECTED_FAKE: "I couldn't confirm that this is a genuine document. Please upload the original.",
  UNREADABLE: "The image is hard to read. Please upload a clearer photo or scan.",
  UNSUPPORTED_FILE: "I can't read that kind of file. Please upload a JPG, PNG or PDF.",
  IDENTITY_MISMATCH: "I couldn't match this document to you. Please upload your own.",
  RESUME_NAME: "The name on this resume doesn't match. Please upload the correct resume.",
};

// Refusals name the document, so what she says matches what is on screen: "This looks like a PAN
// card, not an Aadhaar card." One recording per combination.

/** The slot a file was uploaded to, for the documents that are checked on upload. */
export type VerifiedDoc = "aadhaar" | "pan" | "resume" | "marksheet" | "degree" | "bank";
const VERIFIED_LABELS: Record<VerifiedDoc, string> = {
  aadhaar: "Aadhaar card",
  pan: "PAN card",
  resume: "resume",
  marksheet: "marksheet",
  degree: "degree certificate",
  bank: "cheque or bank passbook",
};

/** What a refused file turned out to be. "unknown" covers anything else: a photo, a passport... */
type SeenDoc = VerifiedDoc | "unknown";
const SEEN_LABELS: Record<SeenDoc, string> = { ...VERIFIED_LABELS, unknown: "unknown document" };

const withArticle = (label: string): string => `${/^[aeiou]/i.test(label) ? "an" : "a"} ${label}`;

/** Maps the checker's free-text "detectedType" (e.g. "PAN card", "photo") onto the documents she can name. */
export const seenDoc = (detectedType: string | undefined): SeenDoc => {
  const t = (detectedType ?? "").toLowerCase();
  if (/aadh?a?ar/.test(t)) return "aadhaar";
  if (/\bpan\b/.test(t)) return "pan";
  if (/resume|curriculum|\bcv\b/.test(t)) return "resume";
  if (/bank|cheque|passbook|statement/.test(t)) return "bank";
  if (/mark ?sheet|grade|transcript/.test(t)) return "marksheet";
  if (/degree|diploma/.test(t)) return "degree";
  return "unknown";
};

const wrongDocumentLine = (doc: VerifiedDoc, seen: SeenDoc): SirraLine => {
  // A file that was taken for the very document it was uploaded as is not "something else": say it plainly.
  if (seen === "unknown" || seen === doc) {
    return { id: `wrong-unknown-${doc}`, text: `This doesn't look like ${withArticle(VERIFIED_LABELS[doc])}. Please upload the correct document.` };
  }
  return {
    id: `wrong-${seen}-${doc}`,
    text: `This looks like ${withArticle(SEEN_LABELS[seen])}, not ${withArticle(VERIFIED_LABELS[doc])}. Please upload the correct document.`,
  };
};
const fakeLine = (doc: VerifiedDoc): SirraLine => ({
  id: `fake-${doc}`,
  text: `This doesn't look like a genuine ${VERIFIED_LABELS[doc]}. Please upload the original document.`,
});
const unreadableLine = (doc: VerifiedDoc): SirraLine => ({
  id: `unreadable-${doc}`,
  text: `Your ${VERIFIED_LABELS[doc]} is hard to read. Please upload a sharper photo or scan.`,
});

export interface RejectionContext {
  /** The slot the file went into. Without it, the general wording is used. */
  doc?: VerifiedDoc | undefined;
  /** What the checker said the file actually is. */
  detected?: string | undefined;
}

/** What Sirra says when a document is refused. */
export const rejectionLine = (code: string | undefined, ctx: RejectionContext = {}): SirraLine => {
  const { doc, detected } = ctx;
  if (doc && (code === "WRONG_DOCUMENT" || !code || !(code in REJECTIONS))) return wrongDocumentLine(doc, seenDoc(detected));
  if (doc && code === "SUSPECTED_FAKE") return fakeLine(doc);
  if (doc && code === "UNREADABLE") return unreadableLine(doc);
  const key = code === "IDENTITY_MISMATCH" && doc === "resume" ? "RESUME_NAME" : code && code in REJECTIONS ? code : "WRONG_DOCUMENT";
  return { id: `no-${key.toLowerCase().replace(/_/g, "-")}`, text: REJECTIONS[key]! };
};

/** Every line that has recorded audio, for the generator script. */
export function allLines(): SirraLine[] {
  const lines: SirraLine[] = [INTRO];
  for (let i = 0; i < STEP_TEXT.length; i++) lines.push(stepLine(i));
  lines.push(SIGN_DOCUMENTS_LINE);
  for (const doc of Object.keys(DOC_LABELS)) lines.push(docAccepted(doc));
  lines.push(FORM11_RECEIVED, UPLOAD_FAILED, READ_FAILED);
  for (const rule of Object.keys(FILE_RULES) as FileRuleKey[]) lines.push(wrongFileTypeLine(rule));
  for (const code of Object.keys(REJECTIONS)) {
    lines.push({ id: `no-${code.toLowerCase().replace(/_/g, "-")}`, text: REJECTIONS[code]! });
  }
  for (const doc of Object.keys(VERIFIED_LABELS) as VerifiedDoc[]) {
    lines.push(fakeLine(doc), unreadableLine(doc));
    for (const seen of Object.keys(SEEN_LABELS) as SeenDoc[]) lines.push(wrongDocumentLine(doc, seen));
  }
  return [...new Map(lines.map((l) => [l.id, l])).values()];
}
